import { useState } from 'react';
import { Check, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { EQUIPMENT } from '@/data/equipment';
import { ZONES } from '@/data/zones';
import { MAX_PRIORITY_ZONES } from '@/data/constants';
import { calcBedtime, dateToApolloDay } from '@/lib/calc';
import type { CoachStyle, EquipmentId, FitnessLevel, Profile, Sex, ZoneId } from '@/types';

export interface OnboardingProps {
  onComplete: (profile: Profile) => void;
}

interface Draft {
  name: string;
  sex: Sex;
  age: string;
  height: string;
  currentWeight: string;
  goalWeight: string;
  wakeOffice: string;
  wakeHome: string;
  boxingReturn: boolean;
  equipment: EquipmentId[];
  priorityZones: ZoneId[];
  coachStyle: CoachStyle;
  level: FitnessLevel;
}

const initial: Draft = {
  name: '',
  sex: 'h',
  age: '',
  height: '178',
  currentWeight: '',
  goalWeight: '67',
  wakeOffice: '06:00',
  wakeHome: '08:30',
  boxingReturn: true,
  equipment: [],
  priorityZones: [],
  coachStyle: 'pro',
  level: 'intermediate',
};

const LEVELS: { id: FitnessLevel; name: string; sub: string; example: string }[] = [
  {
    id: 'beginner',
    name: 'Débutant',
    sub: 'Tu découvres ou tu reviens de très loin',
    example: '< 5 vraies pompes, jamais de tractions, peu d\'expérience charge.',
  },
  {
    id: 'intermediate',
    name: 'Intermédiaire',
    sub: 'Tu connais les bases',
    example: '10-20 pompes, tractions assistées, squat technique correcte.',
  },
  {
    id: 'advanced',
    name: 'Confirmé',
    sub: 'Tu pousses lourd et propre',
    example: '20+ pompes strictes, 5+ tractions, squat avec charge sérieuse.',
  },
];

export const Onboarding = ({ onComplete }: OnboardingProps) => {
  const [data, setData] = useState<Draft>(initial);
  const [step, setStep] = useState(0);

  const next = () => setStep(s => s + 1);
  const prev = () => setStep(s => s - 1);

  const finish = () => {
    const now = new Date();
    const profile: Profile = {
      name: data.name.trim() || 'Athlète',
      sex: data.sex,
      age: Number(data.age) || 30,
      height: Number(data.height) || 178,
      currentWeight: Number(data.currentWeight) || 0,
      goalWeight: Number(data.goalWeight) || 67,
      wakeOffice: data.wakeOffice,
      wakeHome: data.wakeHome,
      boxingReturn: data.boxingReturn,
      equipment: data.equipment,
      priorityZones: data.priorityZones,
      coachStyle: data.coachStyle,
      level: data.level,
      programStartDate: now.toISOString(),
      // Décision validée : le jour de démarrage utilisateur devient son jour 0 du programme,
      // pour ne pas démarrer par un jour de repos quelques jours après l'inscription.
      startDayOfWeek: dateToApolloDay(now),
    };
    onComplete(profile);
  };

  // Validations par étape
  const canProceed = (() => {
    switch (step) {
      case 1: return data.name.trim().length > 0 && Number(data.age) > 0;
      case 2: return Number(data.height) > 0 && Number(data.currentWeight) > 0 && Number(data.goalWeight) > 0;
      case 3: return /^\d{2}:\d{2}$/.test(data.wakeOffice) && /^\d{2}:\d{2}$/.test(data.wakeHome);
      case 5: return data.priorityZones.length >= 3;
      case 6: return !!data.level;
      default: return true;
    }
  })();

  const steps: { title: string; sub?: string; body: React.ReactNode }[] = [
    {
      title: 'Bienvenue dans APOLLO',
      sub: "L'art de bâtir un corps adulte, dessiné, durable.",
      body: (
        <div className="space-y-3">
          <p className="text-zinc-400 text-sm leading-relaxed">
            Cette application va t'accompagner chaque jour : entraînements progressifs adaptés à ta reprise de boxe, nutrition pour prise de masse sèche, suivi corporel précis, et tips de mode de vie.
          </p>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Quelques infos pour calibrer ton programme.
          </p>
        </div>
      ),
    },
    {
      title: 'Identité',
      body: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="ob-name">Prénom</Label>
            <Input id="ob-name" value={data.name} onChange={e => setData({ ...data, name: e.target.value })} placeholder="Ton prénom" />
          </div>
          <div>
            <Label htmlFor="ob-age">Âge</Label>
            <Input id="ob-age" type="number" numeric value={data.age} onChange={e => setData({ ...data, age: e.target.value })} placeholder="30" />
          </div>
          <div>
            <Label>Sexe</Label>
            <div className="flex gap-2">
              <Button variant={data.sex === 'h' ? 'primary' : 'secondary'} onClick={() => setData({ ...data, sex: 'h' })} fullWidth>Homme</Button>
              <Button variant={data.sex === 'f' ? 'primary' : 'secondary'} onClick={() => setData({ ...data, sex: 'f' })} fullWidth>Femme</Button>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Corps',
      body: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="ob-height">Taille (cm)</Label>
            <Input id="ob-height" type="number" numeric value={data.height} onChange={e => setData({ ...data, height: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="ob-weight">Poids actuel (kg)</Label>
            <Input id="ob-weight" type="number" decimal step="0.1" value={data.currentWeight} onChange={e => setData({ ...data, currentWeight: e.target.value })} placeholder="ex : 62.5" />
          </div>
          <div>
            <Label htmlFor="ob-goal">Poids cible (kg)</Label>
            <Input id="ob-goal" type="number" decimal step="0.1" value={data.goalWeight} onChange={e => setData({ ...data, goalWeight: e.target.value })} />
            <p className="text-xs text-zinc-500 mt-2">67 kg pour 1m78 = IMC 21.1 (sec, défini)</p>
          </div>
        </div>
      ),
    },
    {
      title: 'Rythme',
      body: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="ob-wo">Réveil — jours de bureau</Label>
            <Input id="ob-wo" type="time" value={data.wakeOffice} onChange={e => setData({ ...data, wakeOffice: e.target.value })} />
            <p className="text-xs text-zinc-500 mt-2 tabular-nums">Couché conseillé : {calcBedtime(data.wakeOffice)}</p>
          </div>
          <div>
            <Label htmlFor="ob-wh">Réveil — télétravail / weekend</Label>
            <Input id="ob-wh" type="time" value={data.wakeHome} onChange={e => setData({ ...data, wakeHome: e.target.value })} />
            <p className="text-xs text-zinc-500 mt-2 tabular-nums">Couché conseillé : {calcBedtime(data.wakeHome)}</p>
          </div>
        </div>
      ),
    },
    {
      title: 'Matériel',
      sub: 'On adaptera tes exos en fonction.',
      body: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {EQUIPMENT.map(eq => {
              const selected = data.equipment.includes(eq.id);
              return (
                <button
                  key={eq.id}
                  aria-pressed={selected}
                  onClick={() => {
                    const nextEq = selected
                      ? data.equipment.filter(e => e !== eq.id)
                      : [...data.equipment, eq.id];
                    setData({ ...data, equipment: nextEq });
                  }}
                  className={[
                    'min-h-tap px-3 py-3 rounded-xl border text-sm flex items-center gap-2 transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
                    selected
                      ? 'bg-accent-500 text-zinc-950 border-accent-500 font-medium'
                      : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700',
                  ].join(' ')}
                >
                  <span className="text-lg" aria-hidden="true">{eq.emoji}</span>
                  <span className="flex-1 text-left">{eq.n}</span>
                  {selected && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-500">{data.equipment.length} équipement(s) sélectionné(s)</p>
        </div>
      ),
    },
    {
      title: 'Zones à développer',
      sub: 'Choisis 3 à 5 zones prioritaires.',
      body: (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {ZONES.map(z => {
              const selected = data.priorityZones.includes(z.id);
              const disabled = !selected && data.priorityZones.length >= MAX_PRIORITY_ZONES;
              return (
                <button
                  key={z.id}
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() => {
                    const nextZ = selected
                      ? data.priorityZones.filter(e => e !== z.id)
                      : [...data.priorityZones, z.id];
                    setData({ ...data, priorityZones: nextZ });
                  }}
                  className={[
                    'min-h-tap px-3 py-3 rounded-xl border text-sm flex items-center gap-2 transition-colors disabled:opacity-30',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
                    selected
                      ? 'bg-accent-500 text-zinc-950 border-accent-500 font-medium'
                      : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700',
                  ].join(' ')}
                >
                  <span className="text-lg" aria-hidden="true">{z.emoji}</span>
                  <span className="flex-1 text-left">{z.n}</span>
                  {selected && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-500 tabular-nums">{data.priorityZones.length} / {MAX_PRIORITY_ZONES} sélectionnées</p>
          {data.priorityZones.length < 3 && (
            <p className="text-xs text-amber-400">Choisis au moins 3 zones pour personnaliser ton programme.</p>
          )}
        </div>
      ),
    },
    {
      title: 'Niveau de forme',
      sub: 'On adaptera les exos et le volume.',
      body: (
        <div className="space-y-2">
          {LEVELS.map(l => {
            const selected = data.level === l.id;
            return (
              <button
                key={l.id}
                onClick={() => setData({ ...data, level: l.id })}
                aria-pressed={selected}
                className={[
                  'w-full min-h-tap px-4 py-3 rounded-xl border text-left transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60',
                  selected
                    ? 'bg-accent-500 text-zinc-950 border-accent-500'
                    : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700',
                ].join(' ')}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-medium">{l.name}</span>
                  {selected && <Check className="w-4 h-4" />}
                </div>
                <div className={`text-xs ${selected ? 'text-zinc-700' : 'text-zinc-500'}`}>{l.sub}</div>
                <div className={`text-[11px] mt-1 italic ${selected ? 'text-zinc-700' : 'text-zinc-600'}`}>{l.example}</div>
              </button>
            );
          })}
          <p className="text-xs text-zinc-500 leading-relaxed pt-2">
            Modifiable à tout moment depuis le Profil.
          </p>
        </div>
      ),
    },
    {
      title: 'Programme',
      body: (
        <div className="space-y-4">
          <Card className="bg-accent-500/5 border-accent-500/30">
            <p className="text-sm text-amber-200 leading-relaxed">
              <strong>Programme de reprise (4 semaines)</strong> calibré pour une remise en forme, suivi d'un cycle de croisière progressif. Aujourd'hui devient ton <strong>jour 0</strong>.
            </p>
          </Card>
          <div className="space-y-2 text-sm text-zinc-400">
            <Step text="Semaine 1 — Réveil : volume faible, technique" />
            <Step text="Semaine 2 — Reprise : volume +20%" />
            <Step text="Semaine 3 — Construction : rounds standards" />
            <Step text="Semaine 4+ — Rythme de croisière, progression par charges" />
          </div>
        </div>
      ),
    },
  ];

  const cur = steps[step]!;
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col pt-safe pb-safe pl-safe pr-safe">
      <div className="flex-1 px-5 py-8 max-w-md mx-auto w-full flex flex-col">
        <div className="mb-8">
          <div className="flex items-center gap-1.5 mb-6" role="progressbar" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={step + 1}>
            {steps.map((_, i) => (
              <div
                key={i}
                className={[
                  'h-1 flex-1 rounded-full transition-colors',
                  i <= step ? 'bg-accent-500' : 'bg-zinc-800',
                ].join(' ')}
              />
            ))}
          </div>
          <h1 className="text-2xl font-light tracking-tight">{cur.title}</h1>
          {cur.sub && <p className="text-zinc-500 text-sm mt-1">{cur.sub}</p>}
        </div>

        <div className="flex-1 animate-slide-up" key={step}>{cur.body}</div>

        <div className="flex gap-2 mt-8">
          {step > 0 && (
            <Button variant="secondary" iconOnly aria-label="Étape précédente" onClick={prev}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          {isLast
            ? <Button variant="primary" onClick={finish} fullWidth disabled={!canProceed}>Lancer APOLLO</Button>
            : <Button variant="primary" onClick={next} fullWidth disabled={!canProceed}>Continuer</Button>
          }
        </div>
      </div>
    </div>
  );
};

const Step = ({ text }: { text: string }) => (
  <div className="flex items-start gap-2">
    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
    <span><strong className="text-zinc-200">{text.split('—')[0]?.trim()}</strong> — {text.split('—')[1]?.trim()}</span>
  </div>
);
